import { Box, Button, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

export const AutoTranslateButton = styled(Button)({
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: 8,
  padding: '4px 10px',
  minHeight: 'unset',
  height: 'auto',
  lineHeight: 'normal',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  border: 'var(--am-inline-btn-border)',
  backgroundColor: 'var(--am-inline-btn-bg)',
  color: 'var(--am-inline-btn-color)',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
    backgroundColor: 'var(--am-inline-btn-hover-bg)',
    border: 'var(--am-inline-btn-border)',
  },
  '&.Mui-disabled': {
    opacity: 0.5,
    borderColor: 'var(--am-border-mid)',
    color: 'var(--am-text-primary)',
  },
});

export const LanguageRowsList = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$itemsPerRow',
})<{ $itemsPerRow: number }>(({ $itemsPerRow }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${$itemsPerRow}, 1fr)`,
  gap: 8,
}));

export const LanguageRowItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected?: boolean }>(({ $selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '13px 16px',
  borderRadius: 11,
  cursor: 'pointer',
  backgroundColor: $selected ? 'var(--am-accent-surface)' : 'var(--am-surface)',
  border: `1.5px solid ${$selected ? 'var(--am-accent-text)' : 'var(--am-border)'}`,
  transition: 'all 0.12s',
}));

export const LanguageRowFlag = styled('span')({
  fontSize: 20,
  flexShrink: 0,
});

export const LanguageRowName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--am-text-primary)',
  flex: 1,
}) as typeof Typography;

export const BaseBadge = styled(Typography)({
  display: 'inline-flex',
  alignItems: 'center',
  height: 22,
  flexShrink: 0,
  boxSizing: 'border-box',
  fontSize: '10px',
  fontWeight: 700,
  color: 'var(--am-text-disabled)',
  backgroundColor: 'var(--am-surface-2)',
  border: '1px solid var(--am-border)',
  borderRadius: 4,
  padding: '0 8px',
}) as typeof Typography;

export const ProgressWrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: 22,
  gap: 10,
  flexShrink: 0,
});

export const ProgressTrack = styled(Box)({
  width: 90,
  height: 6,
  borderRadius: 3,
  // --am-surface-2 is too close to the row's own --am-surface background in
  // light theme to read as a track at all — border-mid has real contrast in
  // both themes.
  backgroundColor: 'var(--am-border-mid)',
  overflow: 'hidden',
});

export const ProgressFill = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$state',
})<{ $state: 'done' | 'partial' | 'empty' }>(({ $state }) => ({
  height: '100%',
  borderRadius: 3,
  transition: 'width 0.3s',
  backgroundColor:
    $state === 'done'
      ? 'var(--am-success-color)'
      : $state === 'partial'
      ? 'var(--am-warning-color)'
      : 'var(--am-border-mid)',
}));

export const PreviewIconButton = styled(IconButton)({
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: 8,
  color: 'var(--am-ghost-btn-color)',
  border: 'var(--am-ghost-btn-border)',
  '&:hover': {
    backgroundColor: 'var(--am-ghost-btn-hover-bg)',
  },
  '&.Mui-disabled': {
    opacity: 0.5,
  },
});

export const ProgressCountText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$state',
})<{ $state: 'done' | 'partial' | 'empty' }>(({ $state }) => ({
  fontSize: '12px',
  fontWeight: 700,
  width: 32,
  textAlign: 'right',
  color:
    $state === 'done'
      ? 'var(--am-success-color)'
      : $state === 'partial'
      ? 'var(--am-warning-color)'
      : 'var(--am-text-disabled)',
}));
